// List-view row for a hosted site — status, size/files/date, actions, and a details disclosure (description, summary, versions)

import { ChevronDown, FileCode } from 'lucide-react'
import type { HostedSite } from '../../hooks/use-hosted-sites'
import { useAppStore } from '../../stores/app-store'
import { useT } from '../../i18n'
import { cn } from '../../lib/utils'
import { cardClass } from '../ui/styles'
import { SiteActions } from './site-actions'
import { SiteDisclosure, type SiteDetailsProps } from './site-disclosure'
import { formatBytes, formatDate, sitePublicUrl } from './utils'

export interface HostedSiteRowProps extends SiteDetailsProps {
  site: HostedSite
  expanded: boolean
  onToggleExpanded: (id: string) => void
  copied: boolean
  onCopyLink: (site: HostedSite) => void
  onPreview: (site: HostedSite) => void
  onDelete: (site: HostedSite) => void
}

const STATUS_CLASS: Record<HostedSite['status'], string> = {
  ready: 'bg-success-soft text-success',
  processing: 'bg-warning-soft text-warning',
  failed: 'bg-danger-soft text-danger',
}

export function SiteStatusBadge({ status }: { status: HostedSite['status'] }) {
  const t = useT()
  return (
    <span className={cn('inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-xs font-medium', STATUS_CLASS[status] ?? STATUS_CLASS.processing)}>
      {t(`sites.status.${status}` as const)}
    </span>
  )
}

/** "12.3 KB · 4 files · Oct 2, 2026 · v1.0.0" */
export function SiteMeta({ site, className }: { site: HostedSite; className?: string }) {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  return (
    <p className={cn('flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-fg-subtle', className)}>
      <span>{formatBytes(site.totalSizeBytes)}</span>
      <span aria-hidden="true">·</span>
      <span>{t(site.fileCount === 1 ? 'sites.filesOne' : 'sites.files', { count: site.fileCount })}</span>
      <span aria-hidden="true">·</span>
      <span>{formatDate(site.createdAt, locale)}</span>
      {site.version && (
        <>
          <span aria-hidden="true">·</span>
          <span>{t('sites.versions.version', { version: site.version })}</span>
        </>
      )}
    </p>
  )
}

export function DetailsToggle({ site, expanded, onToggle, className }: { site: HostedSite; expanded: boolean; onToggle: () => void; className?: string }) {
  const t = useT()
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={t(expanded ? 'sites.details.hideNamed' : 'sites.details.showNamed', { name: site.filename })}
      className={cn('inline-flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-xs text-fg-muted hover:bg-surface-hover hover:text-fg', className)}
    >
      {expanded ? t('sites.details.hide') : t('sites.details.show')}
      <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')} aria-hidden="true" />
    </button>
  )
}

export function HostedSiteRow({ site, expanded, onToggleExpanded, copied, onCopyLink, onPreview, onDelete, ...details }: HostedSiteRowProps) {
  return (
    <li className={cn(cardClass, 'p-3')}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-surface-muted">
            <FileCode className={cn('h-4 w-4', site.status === 'ready' ? 'text-accent-text' : 'text-fg-subtle')} aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <p className="truncate text-sm font-medium text-fg" title={site.filename}>
                {site.filename}
              </p>
              <SiteStatusBadge status={site.status} />
            </div>
            <SiteMeta site={site} />
          </div>
        </div>
        <div className="flex items-center justify-between gap-1 sm:justify-end">
          <DetailsToggle site={site} expanded={expanded} onToggle={() => onToggleExpanded(site.id)} />
          <SiteActions site={site} publicUrl={sitePublicUrl(site)} copied={copied} onCopyLink={onCopyLink} onPreview={onPreview} onDelete={onDelete} />
        </div>
      </div>
      {site.description && !expanded && <p className="mt-2 line-clamp-2 text-sm text-fg-muted">{site.description}</p>}
      {expanded && <SiteDisclosure site={site} {...details} />}
    </li>
  )
}
